import React, { useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChalkboard, faLock, faUserGraduate } from "@fortawesome/free-solid-svg-icons";

import { capitalizeFirstLetters, sortArrayOfObjects } from "../../../../js/Helpers";
import { getLabelColorClass, INCLUSION_LABEL_COLOR } from "../../utils/labelColorUtils";

const INCLUSION_SUBSECTION_TEXT_CLASS = `${INCLUSION_LABEL_COLOR}-text`;
const QUARTERS = ["1", "2", "3", "4"];
const CYCLE_QUARTERS = ["1", "2", "3", "4", "5", "6"];

const sectionHasCycleQuarterDays = (section_info = {}) =>
  (Array.isArray(section_info.quarter_days) ? section_info.quarter_days : []).some((quarter_day) =>
    ["5", "6"].includes(`${quarter_day.quarter}`)
  );

export const getStudentMainSectionsForCoursePeriod = ({
  studentId,
  coursePeriodId,
  studentSectionsByStudentCoursePeriod = {},
  sectionsById = {},
  subsectionParentById = {},
}) => {
  if (!studentId || !coursePeriodId) return [];

  const data_sections = studentSectionsByStudentCoursePeriod[`${studentId}::${coursePeriodId}`] || [];
  const logged_section_ids = new Set();

  return data_sections.reduce((results, section) => {
    const section_id = `${section.section_id}`;
    const main_section_id = subsectionParentById[section_id] || section_id;

    if (logged_section_ids.has(main_section_id)) return results;
    logged_section_ids.add(main_section_id);

    results.push(sectionsById[main_section_id] || section);
    return results;
  }, []);
};

const getSortedLabels = (label_ids = [], labelsById = {}) =>
  sortArrayOfObjects(
    label_ids
      .map((label_id) => labelsById[`${label_id}`])
      .filter(Boolean),
    "label",
    "text",
    "asc"
  );

const StudentMagnetboardSection = React.memo(function StudentMagnetboardSection({
  sectionInfo,
  scheduleInfo,
  scheduleType,
  currentViewType,
  currentDay,
  studentID,
  lockedSectionIds = [],
  sectionsById = {},
  teachersById = {},
  coursesById = {},
  classroomsById = {},
  labelsById = {},
  studentConflictSectionLookup,
  magnetboardOptions = {},
  toggleEditSection,
  isPrintMode = false,
  printDisplayOptions = null,
}) {
  const safe_section_info = sectionInfo || {};
  const subsection_infos = useMemo(
    () =>
      (Array.isArray(safe_section_info.subsections) ? safe_section_info.subsections : [])
        .map((subsection_id) => sectionsById[`${subsection_id}`])
        .filter(Boolean),
    [safe_section_info.subsections, sectionsById]
  );

  const teacher_info = teachersById[`${safe_section_info.teacher_id}`] || {
    name: null,
    first_name: null,
  };
  const course_info = coursesById[`${safe_section_info.course_id}`];
  const classroom_info = classroomsById[`${safe_section_info.classroom_id}`];

  const course_name =
    safe_section_info.section_type === "2"
      ? "Prep"
      : safe_section_info.section_type === "3"
      ? "Duty"
      : course_info
      ? `${capitalizeFirstLetters(course_info.name)} (${course_info.course_code})`
      : null;

  const classroom_name = classroom_info ? classroom_info.classroom_name : null;
  const student_count =
    (Array.isArray(safe_section_info.student_list) ? safe_section_info.student_list.length : 0) +
    subsection_infos.reduce((total_students, subsection_info) => {
      const subsection_student_list = Array.isArray(subsection_info.student_list)
        ? subsection_info.student_list
        : [];
      return total_students + subsection_student_list.length;
    }, 0);

  const quarter_days = Array.isArray(safe_section_info.quarter_days)
    ? safe_section_info.quarter_days
    : [];
  const quarter_set = new Set(quarter_days.map((quarter_day) => `${quarter_day.quarter}`));
  const cycle_course_ids = useMemo(() => {
    return Object.values(sectionsById || {}).reduce((results, section_info) => {
      if(section_info?.course_id && sectionHasCycleQuarterDays(section_info)) results.add(`${section_info.course_id}`);
      return results;
    }, new Set());
  }, [sectionsById]);
  const display_quarters = safe_section_info?.course_id && cycle_course_ids.has(`${safe_section_info.course_id}`)
    ? CYCLE_QUARTERS
    : QUARTERS;
  const quarter_label_prefix = display_quarters.length > 4 ? "C" : "Q";
  const unique_days = [...new Set(quarter_days.map((quarter_day) => `${quarter_day.day}`))];

  if (
    safe_section_info.is_lab === "1" &&
    scheduleType === "block" &&
    currentViewType === "day" &&
    unique_days.length > 0 &&
    !unique_days.includes(`${currentDay}`)
  ) {
    return null;
  }

  const sorted_labels = getSortedLabels(safe_section_info.labels, labelsById);
  const show_section_conflict_indicators = magnetboardOptions.show_section_conflict_indicators !== false;
  const can_open_section = !isPrintMode && magnetboardOptions.allow_section_roster_editing === true && typeof toggleEditSection === "function";
  const showClassroomDetail = !printDisplayOptions || printDisplayOptions.showClassroom === true;
  const showStudentCountDetail = !printDisplayOptions || printDisplayOptions.showStudentCount === true;
  const showSectionDetailRow = safe_section_info.section_type === "1" && (
    printDisplayOptions
      ? (showClassroomDetail || showStudentCountDetail)
      : !isPrintMode
  );
  const showConflictsWarnings = !printDisplayOptions || printDisplayOptions.showConflictsWarnings !== false;
  const has_hard_conflict = showConflictsWarnings && show_section_conflict_indicators && !!studentConflictSectionLookup?.has(
    `${studentID}::${safe_section_info.section_id}`
  );
  const section_part_ids = [
    `${safe_section_info.section_id || ""}`,
    ...subsection_infos.map((subsection_info) => `${subsection_info.section_id || ""}`),
  ].filter(Boolean);
  const locked_section_id_set = new Set((Array.isArray(lockedSectionIds) ? lockedSectionIds : []).map((section_id) => `${section_id}`));
  const student_section_is_locked = section_part_ids.some((section_id) => locked_section_id_set.has(section_id));
  const section_content_classes = [
    "mb-section-content",
    has_hard_conflict ? "mb-section-conflict" : "",
    !has_hard_conflict && student_section_is_locked ? "mb-section-student-locked" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className="mb-student-section"
      data-sectionid={safe_section_info.section_id}
      onDoubleClick={can_open_section ? () => toggleEditSection({
        section_id: safe_section_info.section_id,
        data_id: studentID,
        section_type: safe_section_info.section_type,
        course_period_id: null,
      }) : undefined}
    >
      <div className={section_content_classes}>
        <div className="tooltip" data-tooltip-text={course_name}>
          <div className="mb-section-name-with-status">
            <div className="mb-section-name ellipsis dark-blue-text">
              {course_name ? course_name : "..."}
            </div>
            {student_section_is_locked && (
              <span className="mb-section-lock-status-badge" title="Student section locked">
                <FontAwesomeIcon icon={faLock} />
              </span>
            )}
          </div>
        </div>
        {sorted_labels.map((label) => (
          <div className="mb-section-label extra-small-text" key={label.label_id}>
            <div className={`mb-section-label-icon ${getLabelColorClass(label)}`}></div>
            <div>{label.label}</div>
          </div>
        ))}
        {subsection_infos.map((subsection_info) => {
          const subsection_course_info = coursesById[`${subsection_info.course_id}`];
          const subsection_course_name = subsection_course_info
            ? capitalizeFirstLetters(subsection_course_info.name)
            : null;
          const subsection_course_code = subsection_course_info
            ? subsection_course_info.course_code
            : null;
          const subsection_is_inclusion = subsection_info.is_inclusion || "0";
          const is_placeholder_inclusion =
            scheduleInfo.separate_ICR_codes === "0" &&
            `${safe_section_info.course_id}` === `${subsection_info.course_id}` &&
            subsection_is_inclusion === "1";
          const subsection_labels = getSortedLabels(subsection_info.labels, labelsById);
          const subsection_text_color =
            subsection_is_inclusion === "1" ? INCLUSION_SUBSECTION_TEXT_CLASS : "blue-text";

          return (
            <React.Fragment key={`${safe_section_info.section_id}-${subsection_info.section_id}`}>
              {is_placeholder_inclusion ? (
                <div className={INCLUSION_SUBSECTION_TEXT_CLASS}>Inclusion</div>
              ) : (
                <>
                  {subsection_info.course_id && (
                    <div
                      className="tooltip"
                      data-tooltip-text={`(${subsection_course_code}) ${
                        subsection_course_name
                          ? capitalizeFirstLetters(subsection_course_name)
                          : ""
                      }`}
                    >
                      <div className={`mb-section-name ellipsis ${subsection_text_color}`}>
                        {subsection_course_name &&
                          capitalizeFirstLetters(subsection_course_name)}{" "}
                        {subsection_course_code && `(${subsection_course_code})`}
                      </div>
                    </div>
                  )}
                  {subsection_labels.map((label) => (
                    <div className="mb-section-label extra-small-text" key={label.label_id}>
                      <div className={`mb-section-label-icon ${getLabelColorClass(label)}`}></div>
                      <div className={subsection_text_color}>{label.label}</div>
                    </div>
                  ))}
                </>
              )}
            </React.Fragment>
          );
        })}
        {teacher_info && teacher_info.name && teacher_info.first_name && (
          <div className="mb-section-teacher-name">
            {teacher_info.name}, {teacher_info.first_name}
          </div>
        )}
        {!isPrintMode && (
          <div className={`mb-section-quarter-day-container ${display_quarters.length > 4 ? "mb-section-quarter-day-container-cycle" : ""}`}>
            {display_quarters.map((quarter) => (
              <div
                className={`mb-section-quarter-day ${
                  quarter_set.has(quarter) ? "mb-section-quarter-day-selected" : ""
                }`}
                key={`${safe_section_info.section_id}-quarter-${quarter}`}
              >
                {`${quarter_label_prefix}${quarter}`}
              </div>
            ))}
          </div>
        )}
        {!isPrintMode &&
          (safe_section_info.is_lab === "1" ? (
            <div className="mb-lab-days extra-small-text green-text">
              {unique_days.length > 0 ? `Day(s) ${unique_days.map((day) => day)}` : "No lab days"}
            </div>
          ) : scheduleType === "daily" &&
            unique_days.length > 0 &&
            unique_days.length !== scheduleInfo.num_days_in_cycle ? (
            <div className="mb-lab-days extra-small-text blue-text">
              Day(s) {unique_days.map((day) => day)}
            </div>
          ) : null)}
        {showSectionDetailRow && (
          <div className="mb-section-subname-container extra-small-text">
            {showClassroomDetail && (
              <div className="mb-section-subname">
                <FontAwesomeIcon icon={faChalkboard} />
                {classroom_name ? classroom_name : "--"}
              </div>
            )}
            {showStudentCountDetail && (
              <div className="mb-section-students">
                <FontAwesomeIcon icon={faUserGraduate} /> {student_count}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

export default StudentMagnetboardSection;
